import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';

import { useSelectionChannel } from '../stores/useSelectionChannel';

import type { SelectionMode } from '../stores/useSelectionChannel';

/**
 * Bulk selection debounce delay (ms)
 *
 * select-all / range-select 등 2개 이상의 동시 변경만 debounce.
 * 단일 변경(1:1 replace, 단일 추가/해제)은 즉시 반영.
 *
 * 200ms: 사용자의 연속 클릭(대략 150~250ms 간격) 구간을 포괄하여
 * 인접한 bulk 토글을 하나로 합쳐 깜빡임을 최소화.
 */
const BULK_DEBOUNCE_MS = 200;

function areArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((item, index) => item === b[index]);
}

/**
 * 배열 참조 안정화 훅
 *
 * 배열 내용이 동일하면 이전 참조를 유지하여 불필요한 useEffect 실행 방지.
 * React Query의 structural sharing 패턴에서 영감을 받음.
 *
 * @see https://tkdodo.eu/blog/react-query-render-optimizations
 */
function useStableArray(array: string[]): string[] {
  const ref = useRef(array);

  if (!areArraysEqual(ref.current, array)) {
    ref.current = array;
  }

  return ref.current;
}

/**
 * items 배열 참조 안정화 훅
 *
 * selectedIds가 동일하면 items 참조를 유지합니다.
 * SelectionChannel의 dedup 로직(참조 비교)이 정상 작동하도록 함.
 */
function useStableItems<T>(items: T[] | undefined, stableIds: string[]): T[] | undefined {
  const ref = useRef<{ ids: string[]; items: T[] | undefined }>({
    ids: stableIds,
    items,
  });

  if (!areArraysEqual(ref.current.ids, stableIds)) {
    ref.current = { ids: stableIds, items };
  }

  return ref.current.items;
}

interface UsePanelSelectionSyncOptions<T = unknown> {
  /**
   * 유효한 ID 배열 (현재 화면에 존재하는 ID 목록)
   * - 선택된 ID가 이 목록에 있는지 확인
   * - 없으면 선택이 무효화되고 패널이 닫힘
   */
  availableIds: string[];

  /**
   * Scope 키 (SelectionChannel 식별자)
   */
  scopeKey: string;

  /**
   * 선택된 ID 배열
   */
  selectedIds: string[];

  /**
   * 선택 모드
   * @default 'single'
   */
  mode?: SelectionMode;

  /**
   * 선택 초기화 콜백 (선택적)
   * - 선택된 ID가 availableIds에 없을 때 호출됨
   */
  onResetSelection?: () => void;

  /**
   * 선택된 항목 데이터 배열 (옵션)
   */
  selectedItems?: T[];
}

/**
 * 선택 상태와 패널을 동기화하는 훅
 *
 * @description
 * - 선택 변경 시 SelectionChannel에 publish → 패널 열림
 * - 선택 해제 시 SelectionChannel clear → 패널 닫힘
 * - 변화량 기반 single/bulk 분기: 1개 변경은 즉시, 2개 이상 변경은 200ms debounce
 * - 선택된 ID가 availableIds에 없으면 onResetSelection 호출 → 패널 닫힘
 *
 * @example
 * ```tsx
 * usePanelSelectionSync({
 *   scopeKey: ZONE_LIST_SCOPE.scopeKey,
 *   selectedIds: selectedZones.map(z => z.id),
 *   availableIds: tableData.map(row => row.id),
 *   onResetSelection: tableInstance.resetRowSelection,
 *   mode: 'multi',
 * });
 * ```
 */
export default function usePanelSelectionSync<T = unknown>({
  scopeKey,
  selectedIds: rawSelectedIds,
  selectedItems,
  mode = 'single',
  availableIds: rawAvailableIds,
  onResetSelection,
}: UsePanelSelectionSyncOptions<T>): void {
  const { publishSelection, clearSelection } = useSelectionChannel();

  const selectedIds = useStableArray(rawSelectedIds);
  const availableIds = useStableArray(rawAvailableIds);
  const stableItems = useStableItems(selectedItems, selectedIds);

  const onResetSelectionRef = useRef(onResetSelection);
  onResetSelectionRef.current = onResetSelection;

  // 순서 비결정성 제거 및 same-set 판정용 (SelectionChannel dedup이 index-sensitive이므로 필요)
  const sortedSelectedIds = useMemo(() => [...selectedIds].sort(), [selectedIds]);
  const prevSortedIdsRef = useRef<string[]>([]);
  const prevLengthRef = useRef(0);

  // bulk 변경만 debounce. last-call-wins 의미론으로 마지막 flush 함수만 실행됨
  const debouncedFlush = useMemo(
    () =>
      debounce((fn: () => void) => fn(), BULK_DEBOUNCE_MS, {
        leading: false,
        trailing: true,
      }),
    [],
  );

  useEffect(() => () => debouncedFlush.cancel(), [debouncedFlush]);

  // scopeKey 변경 시 pending cancel + prev 리셋
  useEffect(() => {
    debouncedFlush.cancel();
    prevSortedIdsRef.current = [];
    prevLengthRef.current = 0;
  }, [scopeKey, debouncedFlush]);

  useEffect(() => {
    // same-set reorder는 publish 유발하지 않음
    if (areArraysEqual(prevSortedIdsRef.current, sortedSelectedIds)) return;
    prevSortedIdsRef.current = sortedSelectedIds;

    const nextLen = selectedIds.length;
    const delta = Math.abs(nextLen - prevLengthRef.current);
    prevLengthRef.current = nextLen;

    const flush = () => {
      if (nextLen === 0) {
        clearSelection(scopeKey);
      } else {
        publishSelection(scopeKey, { ids: selectedIds, mode, items: stableItems });
      }
    };

    // bulk 판정: multi 모드 + delta > 1 + 최종이 1개가 아님
    // - 최종 1개는 항상 single 경로 (사용자 단일 선택 = 즉시 반응)
    // - 최종 0 + delta > 1 은 bulk clear (select-all 해제 연타 깜빡임 방지)
    // - 최종 2개 이상 + delta > 1 은 bulk publish (select-all / range-select)
    const isBulk = mode !== 'single' && delta > 1 && nextLen !== 1;
    if (isBulk) {
      debouncedFlush(flush);
    } else {
      debouncedFlush.cancel();
      flush();
    }
  }, [
    scopeKey,
    sortedSelectedIds,
    selectedIds,
    stableItems,
    mode,
    publishSelection,
    clearSelection,
    debouncedFlush,
  ]);

  // availableIds 변경 시 선택 유효성 검사
  useEffect(() => {
    if (selectedIds.length === 0) return;
    const allIdsValid = selectedIds.every(id => availableIds.includes(id));
    if (!allIdsValid) {
      onResetSelectionRef.current?.();
    }
  }, [availableIds, selectedIds]);
}
